import { FIELD_TYPES } from '../../constants/fieldTypes';

/**
 * Creates a new field object with default values
 */
export const createField = function (fieldType) {
  let fieldsLength = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  const fieldTypeData = FIELD_TYPES[fieldType] || {};
  const baseField = {
    id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    label: `${fieldTypeData.label || 'Field'} ${fieldsLength + 1}`,
    type: fieldType,
    placeholder: '',
    required: false,
    helpText: '',
    columnWidth: '1'
  };

  // Add type-specific defaults
  if (['select', 'radio', 'checkbox'].includes(fieldType)) {
    baseField.options = [{
      label: 'Option 1',
      value: 'option1'
    }];
  }
  if (fieldType === 'repeater') {
    baseField.fields = [{
      id: 'item_field',
      label: 'Item Field',
      type: 'text',
      placeholder: '',
      required: false,
      helpText: ''
    }];
  }
  if (fieldType === 'currency') {
    baseField.currency = 'USD';
    baseField.locale = 'en-US';
  }
  if (fieldType === 'price') {
    baseField.currencySymbol = '$';
    baseField.decimalPlaces = 2;
  }
  if (fieldType === 'tel') {
    baseField.countryCode = 'US';
  }
  if (fieldType === 'tabs') {
    baseField.tabs = [{
      id: 'tab1',
      label: 'Tab 1'
    }];
    baseField.tabContent = {}; // { tabId: [{ type: 'text'|'field', value: string, id?: string }] }
  }

  // Initialize fields array for container fields
  if (['tabs'].includes(fieldType)) {
    baseField.fields = []; // Array of field IDs that belong to this container
    baseField.sections = []; // Fields added to tabs
  }
  return baseField;
};

/**
 * Gets available fields for conditions (excludes current field)
 */
export const getAvailableFields = (fields, currentFieldId) => {
  return fields.filter(field => field.id !== currentFieldId).map(field => ({
    id: field.id,
    label: field.label,
    type: field.type
  }));
};

/**
 * Gets operator options based on source field type
 */
export const getOperatorOptions = sourceFieldType => {
  const commonOperators = [{
    value: 'equals',
    label: 'Equals'
  }, {
    value: 'not_equals',
    label: 'Not Equals'
  }, {
    value: 'is_empty',
    label: 'Is Empty'
  }, {
    value: 'is_not_empty',
    label: 'Is Not Empty'
  }];
  switch (sourceFieldType) {
    case 'checkbox':
      return [...commonOperators, {
        value: 'contains',
        label: 'Contains'
      }, {
        value: 'not_contains',
        label: 'Does Not Contain'
      }];
    case 'number':
    case 'range':
    case 'currency':
    case 'price':
      return [...commonOperators, {
        value: 'greater_than',
        label: 'Greater Than'
      }, {
        value: 'less_than',
        label: 'Less Than'
      }, {
        value: 'greater_equal',
        label: 'Greater Than or Equal'
      }, {
        value: 'less_equal',
        label: 'Less Than or Equal'
      }];
    case 'date':
    case 'time':
    case 'datetime-local':
      return [...commonOperators, {
        value: 'after',
        label: 'After'
      }, {
        value: 'before',
        label: 'Before'
      }];
    default:
      return commonOperators;
  }
};

/**
 * Gets validation options based on field type
 */
export const getValidationOptions = fieldType => {
  const commonValidations = ['customMessage'];
  switch (fieldType) {
    case 'text':
    case 'email':
    case 'url':
    case 'tel':
    case 'search':
      return [...commonValidations, 'minLength', 'maxLength', 'pattern'];
    case 'textarea':
      return [...commonValidations, 'minLength', 'maxLength'];
    case 'number':
    case 'range':
    case 'rating':
    case 'currency':
    case 'price':
      return [...commonValidations, 'min', 'max'];
    case 'date':
    case 'time':
    case 'datetime-local':
    case 'month':
    case 'week':
      return [...commonValidations, 'min', 'max'];
    case 'password':
      return [...commonValidations, 'minLength', 'maxLength', 'pattern'];
    case 'file':
    case 'image':
    case 'attachment':
      return [...commonValidations, 'maxSize', 'allowedTypes'];
    case 'select':
    case 'radio':
    case 'checkbox':
    case 'tags':
      return commonValidations;
    case 'switch':
    case 'repeater':
      return [];
    default:
      return commonValidations;
  }
};

/**
 * Gets column width class name
 */
export const getColumnWidthClass = columnWidth => {
  switch (columnWidth) {
    case '1/4':
      return 'field-width-quarter';
    case '1/3':
      return 'field-width-third';
    case '1/2':
      return 'field-width-half';
    default:
      return 'field-width-full';
  }
};

/**
 * Field categories for the palette
 */
export const FIELD_CATEGORIES = {
  basic: ['text', 'email', 'number', 'password', 'url', 'phone', 'search', 'textarea', 'hidden', 'currency', 'price'],
  selection: ['select', 'radio', 'checkbox', 'switch', 'tags'],
  dateTime: ['date', 'time', 'datetime-local', 'month', 'week'],
  fileMedia: ['file', 'attachment', 'image'],
  advanced: ['range', 'rating', 'repeater', /* 'color', */'signature'],
  layout: ['heading', 'paragraph', 'divider', 'tabs']
};

/**
 * Layout & Content field types that don't need certain properties
 */
export const LAYOUT_CONTENT_TYPES = ['heading', 'paragraph', 'divider', 'tabs'];

/**
 * Input field types that support default values
 */
export const INPUT_FIELD_TYPES = ['text', 'email', 'number', 'password', 'url', 'tel', 'search', 'textarea', 'date', 'time', 'datetime-local', 'month', 'week', 'range', 'hidden', 'currency', 'price', 'tags' /* , 'color' */];

/**
 * Selection field types that support default selection
 */
export const SELECTION_FIELD_TYPES = ['select', 'radio', 'checkbox'];